import React, { useEffect, useState, useRef } from 'react';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Modal, Spinner } from 'react-bootstrap';
import moment from 'moment-jalaali';
import { usePayment } from '../hooks/usePayment';
import { setTurnValid, setPaymentData, setPaymentSuccessful, setAuthority } from '../store/slices/paymentSlice';
import { api } from '../services';

export const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [doctorService, setDoctorService] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'online' | 'wallet'

  const {
    paymentData,
    walletBalance,
    isPaymentSuccessful,
    isPageLocked,
    isLoading,
    error,
    authority,
    isTurnValid
  } = useSelector((state) => state.payment);

  const { key, isAuthenticated, userData } = useSelector((state) => state.auth);

  const {
    handlePayment,
    handleWalletPayment,
    verifyPayment,
    fetchUserData,
    fetchDoctorServices
  } = usePayment();

  const paymentDataRef = useRef(paymentData);

  const clearPreviousAppointmentData = () => {
    console.log('[Payment] Clearing previous appointment data from localStorage');
    // پاک کردن اطلاعات نوبت قبلی
    localStorage.removeItem('appointmentData');
    localStorage.removeItem('time');
    localStorage.removeItem('num');
    localStorage.removeItem('date_n');
    localStorage.removeItem('doctor_id');
    localStorage.removeItem('doctor_name');
    localStorage.removeItem('doctor_famil');
    localStorage.removeItem('Price');
    localStorage.removeItem('payment_time');
    localStorage.removeItem('ServiceCategoryID');
    localStorage.removeItem('ServiceDoctorID');
    localStorage.removeItem('isPaymentSuccessful');
    localStorage.removeItem('authority');
    localStorage.removeItem('invoiceData');
    localStorage.removeItem('enableUpload');
  };

  const initializeData = async () => {
    try {
      console.log('[Payment] Initializing data...');
      
      // خواندن اطلاعات نوبت از localStorage
      const appointmentData = localStorage.getItem('appointmentData');
      let storedAppointmentData = null;
      
      if (appointmentData) {
        try {
          storedAppointmentData = JSON.parse(appointmentData);
          console.log('[Payment] Stored appointment data:', storedAppointmentData);
        } catch (error) {
          console.error('[Payment] Error parsing appointment data:', error);
        }
      }

      // خواندن اطلاعات جداگانه از localStorage
      const dateSelected = localStorage.getItem('date_n');
      const timeSelected = localStorage.getItem('time');
      const num = localStorage.getItem('num');
      const price = localStorage.getItem('Price');
      const doctorName = localStorage.getItem('doctor_name');
      const doctorFamil = localStorage.getItem('doctor_famil');
      const serviceCategoryId = localStorage.getItem('ServiceCategoryID');
      const doctorId = localStorage.getItem('doctor_id');
      const serviceDoctorId = localStorage.getItem('ServiceDoctorID');

      console.log('[Payment] LocalStorage values:', {
        dateSelected,
        timeSelected,
        num,
        price,
        doctorName,
        doctorFamil,
        serviceCategoryId,
        doctorId,
        serviceDoctorId
      });

      // بررسی وجود اطلاعات نوبت
      if (!dateSelected && !timeSelected && !doctorId && !serviceDoctorId && !storedAppointmentData) {
        console.warn('[Payment] No appointment data found - redirecting to history');
        navigate('/history');
        return;
      }

      // بررسی وجود key در localStorage
      const key = localStorage.getItem('key');
      if (!key) {
        console.error('[Payment] No key found in localStorage');
        setModalMessage('لطفاً ابتدا وارد حساب کاربری خود شوید.');
        setIsError(true);
        setShowModal(true);
        navigate('/login');
        return;
      }

      console.log('[Payment] Fetching user data with key:', key);
      const userData = await fetchUserData();
      console.log('[Payment] User data response:', userData);

      // بررسی خطای API
      if (userData?.ErrorMessage) {
        console.error('[Payment] API Error:', userData.ErrorMessage);
        // پاک کردن اطلاعات نامعتبر
        localStorage.removeItem('key');
        localStorage.removeItem('phoneNumber');
        localStorage.removeItem('code');
        
        setModalMessage('لطفاً دوباره وارد حساب کاربری خود شوید.');
        setIsError(true);
        setShowModal(true);
        navigate('/login');
        return;
      }

      if (!userData || !userData[0]) {
        console.error('[Payment] No user data received:', userData);
        setModalMessage('خطا در دریافت اطلاعات کاربر. لطفاً دوباره وارد شوید.');
        setIsError(true);
        setShowModal(true);
        navigate('/login');
        return;
      }

      console.log('[Payment] Updating payment data with user info');
      const updated = {
        ...paymentDataRef.current,
        patientName: `${userData[0].Fname} ${userData[0].Lname}`,
        patientId: userData[0].PatientID
      };
      console.log('[Payment] New payment data after user update:', updated);
      dispatch(setPaymentData(updated));
      paymentDataRef.current = updated;

      // استفاده از اطلاعات نوبت ذخیره شده یا اطلاعات جداگانه
      const newPaymentData = {
        ...paymentDataRef.current,
        dateSelected: storedAppointmentData?.date || dateSelected,
        timeSelected: storedAppointmentData?.time || timeSelected,
        num: storedAppointmentData?.num || num,
        price: storedAppointmentData?.price || price,
        doctorName: storedAppointmentData?.doctorName || doctorName,
        doctorFamil: storedAppointmentData?.doctorFamil || doctorFamil,
        serviceCategoryId: storedAppointmentData?.serviceCategoryId || serviceCategoryId,
        doctorId: storedAppointmentData?.doctorId || doctorId,
        serviceDoctorId: storedAppointmentData?.serviceDoctorId || serviceDoctorId,
        serviceCategoryName: storedAppointmentData?.serviceCategoryName || 'نامشخص'
      };

      console.log('[Payment] Final payment data:', newPaymentData);
      
      const isDifferent = Object.keys(newPaymentData).some(
        key => paymentDataRef.current[key] !== newPaymentData[key]
      );
      
      if (isDifferent) {
        console.log('[Payment] Dispatching final payment data update');
        dispatch(setPaymentData(newPaymentData));
        paymentDataRef.current = newPaymentData;
      }

      // اگر اطلاعات دکتر موجود باشد، سرویس‌های دکتر را دریافت کن
      if (newPaymentData.doctorId) {
        console.log('[Payment] Fetching doctor services for doctorId:', newPaymentData.doctorId);
        const service = await fetchDoctorServices(newPaymentData.doctorId);
        console.log('[Payment] Doctor service response:', service);
        setDoctorService(service);

        if (service) {
          console.log('[Payment] Updating payment data with service info');
          const updated = {
            ...paymentDataRef.current,
            serviceCategoryId: service.ServiceCategoryID,
            serviceCategoryName: service.ServiceName,
            price: service.Price,
            doctorName: service.Lname,
            doctorFamil: service.Fname,
            serviceDoctorId: service.ID
          };
          console.log('[Payment] New payment data after service update:', updated);
          dispatch(setPaymentData(updated));
          paymentDataRef.current = updated;
        } else {
          console.error('[Payment] No service data received for doctorId:', newPaymentData.doctorId);
          setModalMessage('خطا در دریافت اطلاعات سرویس پزشک. لطفاً دوباره تلاش کنید.');
          setIsError(true);
          setShowModal(true);
        }
      }

      // اگر پرداخت موفق بوده، اطلاعات فاکتور را به درستی ذخیره کن
      if (isPaymentSuccessful || localStorage.getItem('isPaymentSuccessful') === 'true') {
        const invoiceData = {
          CategoryName: newPaymentData.serviceCategoryName,
          doctorName: newPaymentData.doctorName,
          doctorFamil: newPaymentData.doctorFamil,
          appointmentDate: newPaymentData.dateSelected,
          appointmentTime: newPaymentData.timeSelected,
          patientName: newPaymentData.patientName,
          paymentAmount: newPaymentData.price
        };
        localStorage.setItem('invoiceData', JSON.stringify(invoiceData));
        console.log('[Payment] Saved invoiceData to localStorage:', invoiceData);
      }
    } catch (error) {
      console.error('[Payment] Error initializing data:', error);
      setModalMessage(error.message || 'خطا در دریافت اطلاعات. لطفاً دوباره تلاش کنید.');
      setIsError(true);
      setShowModal(true);
    }
  };

  useEffect(() => {
    // Wait for auth state to be loaded
    if (isAuthLoading) {
      setIsAuthLoading(false);
      return;
    }

    if (!isAuthenticated || !key) {
      // ذخیره اطلاعات نوبت در localStorage قبل از ریدایرکت
      const currentPaymentData = {
        doctorId: paymentData.doctorId,
        serviceDoctorId: paymentData.serviceDoctorId,
        dateSelected: paymentData.dateSelected,
        timeSelected: paymentData.timeSelected,
        num: paymentData.num,
        price: paymentData.price,
        serviceCategoryId: paymentData.serviceCategoryId,
        doctorName: paymentData.doctorName,
        doctorFamil: paymentData.doctorFamil
      };

      // پاک کردن اطلاعات قبلی قبل از ذخیره اطلاعات جدید
      clearPreviousAppointmentData();

      // ذخیره در localStorage
      Object.entries(currentPaymentData).forEach(([key, value]) => {
        if (value) {
          switch(key) {
            case 'dateSelected':
              localStorage.setItem('date_n', value);
              break;
            case 'timeSelected':
              localStorage.setItem('time', value);
              break;
            case 'doctorId':
              localStorage.setItem('doctor_id', value);
              break;
            case 'serviceDoctorId':
              localStorage.setItem('ServiceDoctorID', value);
              break;
            case 'price':
              localStorage.setItem('Price', value);
              break;
            case 'num':
              localStorage.setItem('num', value);
              break;
            case 'serviceCategoryId':
              localStorage.setItem('ServiceCategoryID', value);
              break;
            case 'doctorName':
              localStorage.setItem('doctor_name', value);
              break;
            case 'doctorFamil':
              localStorage.setItem('doctor_famil', value);
              break;
          }
        }
      });

      // تنظیم منبع ورود
      localStorage.setItem('loginSource', 'payment');
      
      // ریدایرکت به صفحه لاگین
      navigate('/login');
      return;
    }

    // کاربر وارد شده؛ بررسی موجود بودن نوبت در ابتدای صفحه پرداخت
    const checkTurn = async () => {
      try {
        let patientID = localStorage.getItem('patientID');
        if (!patientID) {
          const res = await api.get(`Home/GetProfileInfo?SesID=${key}&OID=1`);
          let data = res.data;
          if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch (e) {}
          }
          patientID = data?.[0]?.PatientID || '';
          if (patientID) localStorage.setItem('patientID', patientID);
        }

        const serviceDoctorId = localStorage.getItem('ServiceDoctorID') || paymentData.serviceDoctorId;
        const nobatNumber = localStorage.getItem('num') || paymentData.num;
        const timeSelected = localStorage.getItem('time') || paymentData.timeSelected;
        const dateSelected = localStorage.getItem('date_n') || paymentData.dateSelected;

        // اگر پارامترها ناقص بود، اجازه ادامه داده می‌شود تا initializeData آن را مدیریت کند
        if (!patientID || !serviceDoctorId || !nobatNumber || !timeSelected || !dateSelected) {
          dispatch(setTurnValid(true));
          return;
        }

        const [y, m, d] = moment(dateSelected, 'jYYYY/jM/jD').format('YYYY-MM-DD').split('-');
        const resultDate = `${m}/${d}/${y}`; // MM/DD/YYYY

        const response = await api.get(
          `Home/CheckExistingTurn?SesID=${key}&OID=1&PatientID=${patientID}&ServiceDoctorID=${serviceDoctorId}&NobatNumber=${nobatNumber}&timadate=${timeSelected}&Date=${resultDate}`
        );
        let checkData = response.data;
        if (typeof checkData === 'string') {
          try { checkData = JSON.parse(checkData); } catch (e) {}
        }
        const errorMessageValue = (checkData && typeof checkData === 'object') ? (checkData.ErrorMessage ?? checkData.errormessage ?? checkData.errorMessage) : undefined;
        const isAllowed = errorMessageValue !== undefined ? `${errorMessageValue}` === '0' : `${checkData}` === '0';

        if (isAllowed) {
          dispatch(setTurnValid(true));
        } else {
          navigate('/', { state: { turnExists: true } });
        }
      } catch (e) {
        console.error('[Payment] Error in CheckExistingTurn:', e);
        navigate('/', { state: { turnExists: true } });
      }
    };

    checkTurn();
  }, [dispatch, isAuthenticated, key, navigate, paymentData, isAuthLoading]);

  useEffect(() => {
    if (isTurnValid && isAuthenticated && key) {
      console.log('[Payment] Turn is valid and user is authenticated, initializing data...');
      initializeData();
    }
  }, [isTurnValid, isAuthenticated, key, dispatch]);

  useEffect(() => {
    if (isAuthenticated && key) {
      const fetchReportNobat = async () => {
        try {
          const response = await api.get(`Home/ReportNobat?SDId=${key}&OID=1`);
          let data = response.data;
          if (typeof data === 'string') {
            data = JSON.parse(data);
          }
          console.log('[Payment] ReportNobat API response:', data);
        } catch (error) {
          console.error('[Payment] Error fetching ReportNobat:', error);
        }
      };
      fetchReportNobat();
    }
  }, [isAuthenticated, key]);

  const formatTime = (time) => {
    if (!time || time === 'نامشخص') return 'نامشخص';
    const [hours, minutes] = time.split(':');
    return `${hours}:${minutes.padStart(2, '0')}`;
  };

  const formatRial = (value) => {
    const numericValue = Number(value) || 0;
    try {
      return numericValue.toLocaleString('fa-IR');
    } catch (e) {
      return numericValue.toLocaleString();
    }
  };

  const selectedTurn = paymentData?.num;
  const CategoryName = paymentData?.serviceCategoryName || doctorService?.ServiceName || 'نامشخص';
  // Derive separate doctor fields (first/given name vs family/last name)
  const doctorFamilForInvoice = paymentData?.doctorName || doctorService?.Lname || '';
  const doctorNameForInvoice = paymentData?.doctorFamil || doctorService?.Fname || '';
  // Full name only for on-page display/printing
  const doctorFullName = ( doctorNameForInvoice && doctorFamilForInvoice)
    ? `${doctorNameForInvoice} ${doctorFamilForInvoice} `
    : (doctorService ? `${doctorService.Fname} ${doctorService.Lname}` : 'نامشخص');
  const appointmentDate = paymentData?.dateSelected || 'نامشخص';
  const appointmentTime = formatTime(paymentData?.timeSelected || 'نامشخص');
  const paymentTime = moment().format('jYYYY/jMM/jDD HH:mm');
  const patientName = paymentData?.patientName || (userData ? `${userData.Fname} ${userData.Lname}` : 'نامشخص');
  const paymentAmount = paymentData?.price || doctorService?.Price || 0;

  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    const verifyPaymentStatus = async () => {
      if (searchParams.get('Status') === 'OK' && searchParams.get('Authority')) {
        try {
          console.log('[Payment] Verifying payment with authority:', searchParams.get('Authority'));
          const descParam = searchParams.get('desc');
          console.log('[Payment] Description parameter:', descParam);
          await verifyPayment(searchParams.get('Authority'), descParam);
          
          setModalMessage('پرداخت و رزرو نوبت با موفقیت انجام شد' + " کد پیگیری بانک : " + searchParams.get('Authority'));
          setIsError(false);
          setShowModal(true);
        } catch (error) {
          console.error('[Payment] Error verifying payment:', error);
          setModalMessage(error.message);
          setIsError(true);
          setShowModal(true);
        }

        searchParams.delete('Authority');
        searchParams.delete('Status');
        const newSearch = searchParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, {
          replace: true,
        });
      }
    };

    if (isTurnValid && isAuthenticated && key) {
      verifyPaymentStatus();
    }
  }, [isTurnValid, isAuthenticated, key, searchParams, navigate, location.pathname]);

  const handlePaymentClick = async () => {
    try {
      console.log('[Payment] handlePaymentClick called');
      await handlePayment(doctorService?.Price);
      } catch (error) {
      console.error('[Payment] Error in handlePaymentClick:', error);
      setModalMessage(error.message);
      setIsError(true);
      setShowModal(true);
    }
  };

  const handleWalletPaymentClick = async () => {
    try {
      console.log('[Payment] handleWalletPaymentClick called');
      await handleWalletPayment();
      setModalMessage('پرداخت با کیف پول و رزرو نوبت با موفقیت انجام شد');
      setIsError(false);
      setShowModal(true);
    } catch (error) {
      console.error('[Payment] Error in handleWalletPaymentClick:', error);
      setModalMessage(error.message);
      setIsError(true);
      setShowModal(true);
    }
  };

  const openConfirm = (action) => {
    setPendingAction(action);
    setShowConfirmModal(true);
  };

  const closeConfirm = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const confirmProceed = async () => {
    const action = pendingAction;
    closeConfirm();
    if (action === 'online') {
      await handlePaymentClick();
    } else if (action === 'wallet') {
      await handleWalletPaymentClick();
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setModalMessage('');
    setIsError(false);
    navigate('/history');
  };

  useEffect(() => {
    return () => {
      localStorage.removeItem('enableUpload');
    };
  }, []);

  const handlePrint = () => {
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div dir="rtl" style="padding: 20px; font-family: 'Tahoma', sans-serif;">
        <h2 style="text-align: center; margin-bottom: 20px;">فاکتور پرداخت</h2>
        <div style="margin-bottom: 20px;">
          <p><strong>نوبت انتخابی:</strong> ${CategoryName || 'نامشخص'}</p>
          <p><strong>نام پزشک:</strong> ${doctorFullName || 'نامشخص'}</p>
          <p><strong>تاریخ نوبت:</strong> ${appointmentDate || 'نامشخص'}</p>
          <p><strong>زمان نوبت:</strong> ${appointmentTime}</p>
          <p><strong>نام مراجعه‌کننده:</strong> ${patientName}</p>
          <p><strong>مبلغ پرداختی:</strong> ${paymentAmount !== null && paymentAmount !== undefined ? `${formatRial(paymentAmount)} ریال` : '0 ریال'}</p>
          <p><strong>زمان پرداخت:</strong> ${paymentTime}</p>
          <p><strong>شماره تراکنش:</strong> ${authority || 'نامشخص'}</p>
          <p><strong>وضعیت:</strong> موفق</p>
        </div>
        <div style="text-align: center; margin-top: 30px; font-size: 12px;">
          <p>این فاکتور به صورت خودکار صادر شده و نیاز به مهر و امضا ندارد.</p>
        </div>
      </div>
    `;

    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;

    window.print();

    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  useEffect(() => {
    // اگر پرداخت موفق در localStorage بود، اطلاعات فاکتور را بازیابی کن
    if (localStorage.getItem('isPaymentSuccessful') === 'true') {
      dispatch(setPaymentSuccessful(true));
      dispatch(setAuthority(localStorage.getItem('authority')));
      const invoiceData = localStorage.getItem('invoiceData');
      if (invoiceData) {
        const data = JSON.parse(invoiceData);
        dispatch(setPaymentData({
          ...paymentData,
          serviceCategoryName: data.CategoryName,
          doctorName: data.doctorName,
          doctorFamil: data.doctorFamil,
          dateSelected: data.appointmentDate,
          timeSelected: data.appointmentTime,
          patientName: data.patientName,
          price: data.paymentAmount
        }));
        localStorage.setItem('invoiceData', JSON.stringify(data));
      }
    }
  }, [dispatch]);

  const handleAfterInvoice = () => {
    localStorage.removeItem('isPaymentSuccessful');
    localStorage.removeItem('authority');
    localStorage.removeItem('invoiceData');
    navigate('/history');
  };

  useEffect(() => {
    // اگر پرداخت موفق بوده، initializeData اجرا نشود
    if (isPaymentSuccessful || localStorage.getItem('isPaymentSuccessful') === 'true') {
      return;
    }
    if (isTurnValid && isAuthenticated && key) {
      initializeData();
    }
  }, [isTurnValid, isAuthenticated, key, dispatch, isPaymentSuccessful]);

  // اضافه کردن useEffect جدید برای بررسی اطلاعات نوبت
  useEffect(() => {
    const checkAppointmentData = () => {
      const appointmentData = localStorage.getItem('appointmentData');
      const dateSelected = localStorage.getItem('date_n');
      const timeSelected = localStorage.getItem('time');
      const doctorId = localStorage.getItem('doctor_id');
      const serviceDoctorId = localStorage.getItem('ServiceDoctorID');

      if (!appointmentData && !dateSelected && !timeSelected && !doctorId && !serviceDoctorId) {
        console.log('No appointment data found in localStorage');
        navigate('/history');
      }
    };

    checkAppointmentData();
  }, [navigate]);

  // Only check for invoice data if payment was successful
  if (isPaymentSuccessful || localStorage.getItem('isPaymentSuccessful') === 'true') {
    const invoiceData = localStorage.getItem('invoiceData');
    if (!invoiceData || !authority) {
      navigate('/history');
      return null;
    }
  }

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="container mt-xl-0">
      {/* {paymentData.serviceCategoryId === 48 && (
        <div className="row mb-4 mt-2">
          <div className="col-lg-12">
            <div className="alert alert-info" role="alert">
              <Link to="/edit-profile" className="alert-link">
                برای تکمیل اطلاعات کاربری خود کلیک کنید
              </Link>
            </div>
          </div>دح
        </div>
      )} */}
      
      <style>
        {`
          .equal-height-row {
            display: flex;
            flex-wrap: wrap;
          }
          .equal-height-col {
            display: flex;
            flex-direction: column;
          }
          .equal-height-card {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
          }
          @media (max-width: 767px) {
            .equal-height-row {
              flex-direction: column;
            }
            .equal-height-col {
              margin-bottom: 1rem;
            }
            .equal-height-card {
              height: auto;
            }
          }
          /* Confirm button subtle pulse animation */
          .confirm-pulse:not(:disabled) {
            animation: confirmPulse 1.4s ease-in-out infinite;
          }
          @keyframes confirmPulse {
            0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(13,110,253,0.35); }
            60% { transform: scale(1.03); box-shadow: 0 0 0 10px rgba(13,110,253,0); }
            100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(13,110,253,0); }
          }
        `}
      </style>

      <div className="row mb-4 mt-2">
        <div className="col-lg-12 path">
          <ul>
            <li>
              <Link className="text-decoration-none" to="/">
                صفحه اصلی
              </Link>
              <FontAwesomeIcon icon={faAngleLeft} className="mr-2" />
            </li>
            {location.state?.from && (
              <li>
                <span style={{ cursor: 'pointer' }} onClick={handleBack} className="text-primary">
                  {location.state.from}
                </span>
                <FontAwesomeIcon icon={faAngleLeft} className="mr-2" />
              </li>
            )}
            <li>
              <span>پرداخت آنلاین</span>
            </li>
          </ul>
        </div>
      </div>

      <p style={{ fontSize: '16px', color: '#000' }}>
        نوبت شما هنوز تکمیل نشده برای ادامه پرداخت را انجام دهید!
      </p>

      <div className="row mb-5 equal-height-row">
        <div className="col-md-6 equal-height-col">
          <div className="col-md-12 doc_col">
            <div className="card p-3 shadow rounded-3 equal-height-card animated-title"
            style={{
              cursor: 'pointer',
              justifyContent: 'end'
            }}>
              {/* <p style={{ fontSize: '13px', color: 'red' , textAlign: 'justify' }}>
                شرایط و قوانین:
                <br />
                در صورتی که لغو تا قبل از ۴۸ ساعت مانده به نوبت انجام شود مبلغ پرداخت شده به کیف پول شما برمی‌گردد و می‌توانید از آن برای ثبت نوبت‌های بعدی خود استفاده کنید. در غیر این‌صورت امکان بازگشت وجه مربوط به "نوبت شما" وجود ندارد 1-
                <br />
                مبلغ پرداختی شما شامل هزینه‌های کارمزد و زیرساخت وبسایت  برای رزرو اینترنتی نوبت است. مبلغ ویزیت میبایست به طور کامل در درمانگاه پرداخت شود. 
              </p> */}
              <p style={{ fontSize: '15px', color: 'red' , fontWeight: 'bold' , marginBottom: '10px' , textAlign: 'center' }}>
                شرایط و قوانین:
                </p>
<p style={{ fontSize: '13px', color: 'red' , textAlign: 'justify' }}> 
                1- در صورتی که لغو تا قبل از ۴۸ ساعت مانده به نوبت انجام شود مبلغ پرداخت شده به کیف پول شما برمی‌گردد و می‌توانید از آن برای ثبت نوبت‌های بعدی خود استفاده کنید. در غیر این‌صورت امکان بازگشت وجه مربوط به "نوبت شما" وجود ندارد.
                </p>
<p style={{ fontSize: '13px', color: 'red' , textAlign: 'justify' }}>
               2- مبلغ پرداختی شما شامل هزینه‌های کارمزد و زیرساخت وبسایت  برای رزرو اینترنتی نوبت است. مبلغ ویزیت میبایست به طور کامل در درمانگاه پرداخت شود. 
              </p>
            </div>
          </div>

          <div className="col-md-12 doc_col mt-2">
            <div className="card p-3 shadow rounded-3 equal-height-card">
              <div className="mt-4">
                <p><strong>نوبت انتخابی شما:</strong> {CategoryName || 'نامشخص'}</p>
                <p><strong>{paymentData?.serviceCategoryId === 1 ? 'بینایی سنج' : 'پزشک'}:</strong> {doctorFullName || 'نامشخص'}</p>
                <p><strong>تاریخ نوبت:</strong> {appointmentDate || 'نامشخص'}</p>
                <p><strong>زمان نوبت:</strong> {appointmentTime}</p>
                <p><strong>نام مراجعه‌کننده:</strong> {patientName}</p>
                <p><strong>مبلغ پرداختی:</strong> {paymentAmount !== null && paymentAmount !== undefined ? `${formatRial(paymentAmount)} ریال` : '0 ریال'}</p>
                <p><strong>موجودی کیف پول:</strong> {walletBalance ? `${formatRial(walletBalance)} ریال` : '0 ریال'}</p>
              </div>
              <div className="mt-4">
                <Button
                  onClick={() => openConfirm('online')}
                  className="btn btn-primary mr-2"
                  disabled={!isTurnValid || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      در حال پردازش...
                    </>
                  ) : (
                    'پرداخت آنلاین'
                  )}
                </Button>
                <Button
                  onClick={() => openConfirm('wallet')}
                  className="btn btn-info mr-2"
                  style={{ color: '#fff' }}
                  disabled={!isTurnValid || walletBalance < paymentAmount || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      در حال پردازش...
                    </>
                  ) : (
                    'پرداخت با کیف پول'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 equal-height-col">
          <div className="card-body card p-3 shadow rounded-3 equal-height-card text-center">
            <img
              src="Img/pay.jpg"
              alt="Profile"
              style={{
                width: '100%',
                maxHeight: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        </div>
      </div>

      <Modal
        show={showConfirmModal}
        onHide={closeConfirm}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header  >
          <Modal.Title>تایید پرداخت</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          انجام مراحل پرداخت به منزله پذیرفتن شرایط سایت است
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={confirmProceed}
            disabled={isLoading}
            className="confirm-pulse"
          >
            تایید
          </Button>
          <Button
            variant="secondary"
            onClick={closeConfirm}
            disabled={isLoading}
          >
            بستن
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>{isError ? 'خطا' : 'موفقیت'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={isLoading}
          >
            بستن
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};